import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { addEmailToItem, removeVisitTypeEmails } from "../taskpane"
import { config } from "../../config"
import { Header } from "./Header"
import { RadioButton } from "./RadioButton"
import type { WorkspaceData } from "../../types/workspace"
import { getCurrentUserEmail, fetchWorkspaceData } from "../utils/apiUtils"
import { ErrorComponent } from "./ErrorComponent"
import { SelectedWorkspace } from "./SelectedWorkspace"
import { Footer } from "./Footer"
import { DefaultBadge } from "./DefaultBadge"
import { getVisitTypeLocalization } from "../utils/helper"

const App: React.FC = () => {
  const { t } = useTranslation()
  const DELAY_AMOUNT = 100

  // State for workspace data and loading/error states
  const [workspaceData, setWorkspaceData] = useState<WorkspaceData | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [userEmail, setUserEmail] = useState<string | null>(null)

  // Effect to fetch user email and workspace data on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Wait for Office to be ready (with timeout)
        let attempts = 0
        while (attempts < 50 && (typeof Office === 'undefined' || !Office.context)) {
          await new Promise(resolve => setTimeout(resolve, DELAY_AMOUNT))
          attempts++
        }

        // Get the current user's email
        const email = await getCurrentUserEmail()
        setUserEmail(email)

        // Fetch workspace data from API
        const data = await fetchWorkspaceData(config.apiUrl, email, config.publicApiKey)
        setWorkspaceData(data)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t('error.loadingFailed')
        console.error('Error initializing data:', err)
        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    initializeData()
  }, [])

  // Initialize state with primary workspace and visit type
  const primaryWorkspace = useMemo(
    () => workspaceData?.workspaces.find(w => w.primary),
    [workspaceData]
  )
  const primaryWorkspaceId = useMemo(
    () => primaryWorkspace?.workspace_id || null,
    [primaryWorkspace]
  )
  const primaryVisitType = useMemo(
    () => primaryWorkspace?.visit_types.find(vt => vt.primary),
    [primaryWorkspace]
  )
  const primaryVisitTypeId = useMemo(
    () => primaryVisitType?.visit_type_id || null,
    [primaryVisitType]
  )

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null>(primaryWorkspaceId)
  const [selectedVisitTypeId, setSelectedVisitTypeId] = useState<number | null>(primaryVisitTypeId)
  const [rememberSelection, setRememberSelection] = useState<boolean>(false)

  // Load saved selection from Office.js roaming settings
  const loadSavedSelection = React.useCallback(async () => {
    try {
      const savedSelectionStr = Office.context.roamingSettings.get('rememberedSelection')
      if (savedSelectionStr) {
        const savedSelection = JSON.parse(savedSelectionStr as string)

        // Validate that the saved selection is still valid with current workspace data
        if (workspaceData) {
          const savedWorkspace = workspaceData.workspaces.find(w => w.workspace_id === savedSelection.workspaceId)
          const savedVisitType = savedWorkspace?.visit_types.find(vt => vt.visit_type_id === savedSelection.visitTypeId)

          if (savedWorkspace && savedVisitType) {
            setSelectedWorkspaceId(savedSelection.workspaceId)
            setSelectedVisitTypeId(savedSelection.visitTypeId)
            setRememberSelection(true)
            return true
          } else {
            // Clear invalid saved selection
            Office.context.roamingSettings.remove('rememberedSelection')
            await new Promise<void>((resolve, reject) => {
              Office.context.roamingSettings.saveAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolve()
                } else {
                  reject(new Error(result.error?.message || 'Failed to save settings after clearing invalid selection'))
                }
              })
            })
          }
        }
      }
    } catch (error) {
      console.error('Error loading saved selection:', error)
    }
    return false
  }, [workspaceData])

  // Update selected workspace and visit type when workspaceData loads
  useEffect(() => {
    const initializeSelections = async () => {
      // First try to load saved selection
      const hasSavedSelection = await loadSavedSelection()

      // If no saved selection, fall back to primary selections
      if (!hasSavedSelection) {
        if (primaryWorkspaceId !== null) {
          setSelectedWorkspaceId(primaryWorkspaceId)
        }
        if (primaryVisitTypeId !== null) {
          setSelectedVisitTypeId(primaryVisitTypeId)
        }
      }
    }

    if (workspaceData) {
      initializeSelections()
    }
  }, [workspaceData, primaryWorkspaceId, primaryVisitTypeId, loadSavedSelection])

  // Get current selected workspace and visit types
  const selectedWorkspace = workspaceData?.workspaces.find(w => w.workspace_id === selectedWorkspaceId)
  const availableVisitTypes = selectedWorkspace?.visit_types || []
  const selectedVisitType = availableVisitTypes.find(vt => vt.visit_type_id === selectedVisitTypeId)

  // Handle workspace selection change
  const handleWorkspaceChange = (workspaceId: number) => {
    setSelectedWorkspaceId(workspaceId)
    // Reset visit type selection and select the primary one for the new workspace
    const newWorkspace = workspaceData?.workspaces.find(w => w.workspace_id === workspaceId)
    const newPrimaryVisitType = newWorkspace?.visit_types.find(vt => vt.primary)

    // If no primary visit type found, select the first available visit type
    const visitTypeToSelect = newPrimaryVisitType || newWorkspace?.visit_types[0]
    setSelectedVisitTypeId(visitTypeToSelect?.visit_type_id || null)
  }

  // Handle visit type selection change
  const handleVisitTypeChange = (visitTypeId: number) => {
    setSelectedVisitTypeId(visitTypeId)
  }

  // Handle save selection
  const handleSaveSelection = async () => {
    if (!rememberSelection) {
      // If not remembering selection, just close the task pane
      closeTaskPane()
      return
    }
    if (selectedVisitType?.address && selectedWorkspaceId && selectedVisitTypeId) {
      try {
        // Save to Office.js settings for persistence across sessions
        const savedSelection = {
          workspaceId: selectedWorkspaceId,
          visitTypeId: selectedVisitTypeId,
          address: selectedVisitType.address,
          workspaceName: selectedWorkspace?.workspace_name,
          visitTypeName: selectedVisitType.visit_type_name,
          savedAt: new Date().toISOString()
        }

        Office.context.roamingSettings.set('rememberedSelection', JSON.stringify(savedSelection))
        await new Promise<void>((resolve, reject) => {
          Office.context.roamingSettings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve()
            } else {
              reject(new Error(result.error?.message || 'Failed to save settings'))
            }
          })
        })

        // Ensure the email is added to the current meeting
        await addEmailToItem(selectedVisitType.address)

        // Close the task pane after email addition completes
        closeTaskPane()
      } catch (error) {
        console.error('Error saving selection:', error)
        // Do not close the task pane on error
      }
    }
  }

  // Handle clear selection
  const handleClearSelection = async () => {
    if (workspaceData) {
      try {
        // Get all visit type addresses to remove them
        const allVisitTypeAddresses = workspaceData.workspaces
          .flatMap(workspace => workspace.visit_types)
          .map(visitType => visitType.address)

        // Remove all visit type emails from the meeting
        await removeVisitTypeEmails(allVisitTypeAddresses)

        // Clear all selections (deselect radio buttons)
        setSelectedWorkspaceId(null)
        setSelectedVisitTypeId(null)
        setRememberSelection(false)

        // Close the task pane after email removal completes
        closeTaskPane()
      } catch (error) {
        console.error('Error clearing selection:', error)
        // Do not close the task pane on error
      }
    }
  }

  // Function to close the add-in task pane
  const closeTaskPane = () => {
    try {
      // Use Office.js API to close the task pane container
      Office.context.ui.closeContainer()
    } catch (error) {
      console.error('Unable to close task pane:', error)
      // Fallback: attempt to close the window (may not work in all Office clients)
      try {
        globalThis.window?.close?.()
      } catch (fallbackError) {
        console.log('Fallback close method also failed:', fallbackError)
      }
    }
  }

  // Hook to automatically update email address when selected visit type changes
  useEffect(() => {
    if (workspaceData && selectedVisitType?.address) {
      const updateEmails = async () => {
        try {
          // Get all visit type addresses from all workspaces to remove any previously selected ones
          const allVisitTypeAddresses = workspaceData.workspaces
            .flatMap(workspace => workspace.visit_types)
            .map(visitType => visitType.address)

          // First remove any existing visit type addresses
          await removeVisitTypeEmails(allVisitTypeAddresses)

          // Then add the currently selected address
          await addEmailToItem(selectedVisitType.address)
        } catch (error) {
          console.error('Error updating emails:', error)
        }
      }

      updateEmails()
    }
  }, [selectedVisitType?.address, workspaceData])

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full h-[100vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg mb-2">Loading workspace data...</p>
          {userEmail && <p className="text-sm text-zinc-500 dark:text-zinc-400">User: {userEmail}</p>}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-[100vh] flex flex-col overflow-y-auto">
      <Header />
      <div className="flex-1 m-6">
        {!error && workspaceData?.workspaces.length > 0 && (
          <div className="flex flex-col gap-4">
            <SelectedWorkspace
              workspaceName={selectedWorkspace?.workspace_name}
              address={selectedVisitType?.address}
            />
            <div className="flex flex-col gap-2">
              <h1 className="text-lg">{t('workspace.label')}</h1>
              {workspaceData.workspaces.map((workspace) => (
                <RadioButton
                  key={workspace.workspace_id}
                  id={`workspace-${workspace.workspace_id}`}
                  name="workspace"
                  checked={selectedWorkspaceId === workspace.workspace_id}
                  onChange={() => handleWorkspaceChange(workspace.workspace_id)}
                >
                  {workspace.workspace_name} {workspace.primary && <DefaultBadge />}
                </RadioButton>
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <h1 className="text-lg">{t('visitType.label')}</h1>
              {availableVisitTypes.map((visitType) => (
                <RadioButton
                  key={visitType.visit_type_id}
                  id={`visit-type-${visitType.visit_type_id}`}
                  name="visitType"
                  checked={selectedVisitTypeId === visitType.visit_type_id}
                  onChange={() => handleVisitTypeChange(visitType.visit_type_id)}
                >
                  {getVisitTypeLocalization(visitType.visit_type_name, t)} {visitType.primary && <DefaultBadge />}
                </RadioButton>
              ))}
            </div>
          </div>
        )}
        {error && (
          <ErrorComponent />
        )}
      </div>
      {!error && (
        <Footer
          rememberSelection={rememberSelection}
          onRememberSelectionChange={setRememberSelection}
          onSaveSelection={handleSaveSelection}
          onClearSelection={handleClearSelection}
          isSaveDisabled={
            !selectedVisitType?.address ||
            !selectedWorkspaceId ||
            !selectedVisitTypeId
          }
        />
      )}
    </div>
  )
}

export default App
